import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { getObjectHash } from '@iotv/datamodel';
import { Alert, Snackbar, Typography } from '@mui/material';
import { GridCellEditStopReasons, useGridApiRef, } from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { genericListViewDefinition } from '../../../../components/factories/AttributeDefinitions';
import { SelectAll, StripedDataGrid, radioSelector } from '../../../../components/factories/DataGrid';
import Assembler from '../../../../data/Assembler';
import { useSelectedObjects } from '../../../../hooks/useSelectedObjects';
import { ObjectViewType } from '../../../../types';
import { viewDefinitionToGridColumnMap } from '../transformFns';
import { PaginationMode, getClassDefaultColumnVisibilityModel } from './fns';
import { useColumnVisibilityForDataGrid } from './hooks/useColumnVisibility';
import { useDocumentClientQuery } from './useDocumentClientQueryV3';
const debug = process.env.REACT_APP_DEBUG && false;
const logQueryParamsHash = process.env.REACT_APP_DEBUG && false;
/** TODO: comment this one too */
export function DocClientDataGrid(props) {
    'use no memo';
    // Cannot access refs during render
    // Passing a ref to a function may read its value during render
    const { query, queryParams: queryParamsIn, itemsEditable = false, itemsSelectable = false, selectedTableObjectsRef, dispatchDataGridHash, processRowUpdate, onProcessRowUpdateError, onError, snackBarState, viewDefinition = genericListViewDefinition, columnVisibilityReferences, queryUuid, onRowClick, onSelectedUpdate, getUpdateFn, dispatchCurrentItemCount, filter, initialSort, rowsPerPageOptions = [5, 10, 20, 25, 50, 100, 200], pageSize = 10, autosizeColumns = false, loading = false, } = props;
    const apiRef = useGridApiRef();
    const snackbar = snackBarState?.[0];
    const setSnackbar = snackBarState?.[1];
    const maxCols = 6;
    const viewDefinitionColumnVisibility = Object.fromEntries(Object.keys(viewDefinition)
        .slice(maxCols)
        .map((k) => [k, false]));
    const { dataTableReference = 'DocClientDataGrid', externalColumnVisibility = viewDefinitionColumnVisibility, objectTypeId = 'ModelCommon', } = columnVisibilityReferences ?? {};
    const [columnVisibilityModel, _setColumnVisibilityModel, onColumnVisibilityModelChange] = useColumnVisibilityForDataGrid({
        dataTableReference,
        objectTypeId,
        viewType: ObjectViewType.List,
        externalColumnVisibility: getClassDefaultColumnVisibilityModel(externalColumnVisibility),
    });
    const [page, setPage] = React.useState(0);
    const [currentPageSize, setCurrentPageSize] = useState(pageSize);
    /** queryPathParams can be mutated from outside ( because of eg objects added or removed via app state ), or insside due to paginaation, */
    const [queryParams, setQueryParams] = React.useState(queryParamsIn);
    const queryParamsInHash = getObjectHash(queryParamsIn);
    logQueryParamsHash && console.log(`DocClientDataGrid calcuted queryParamsInHash as:`, queryParamsInHash);
    const [queryParamsHash, setQueryParamsHash] = React.useState(''); // initial value is 0
    const queryHook = useDocumentClientQuery({ query, queryParams, dependencies: [queryParamsHash] });
    const { querySetterGetters: [[items, setItems], [limit, setLimit], [LastEvaluatedKey, setLastEvaluatedKey], [LastEvaluatedKeys, setLastEvaluatedKeys], [ExclusiveStartKey, setExclusiveStartKey],], forceUpdate, } = queryHook;
    const itemsCount = items.length;
    useEffect(() => {
        setQueryParamsHash(getObjectHash(queryParams));
    }, [queryParams]);
    /** possible issue, when ExclusiveStartKey is set internally, but user changes external query params, does the ExclusiveStartKey reset? */
    useEffect(() => {
        logQueryParamsHash && console.log(`DocClientDataGrid rendering on queryParamsInHash as it may have been mutated by parent component`);
        setQueryParams({ ...queryParamsIn, ExclusiveStartKey: queryParams?.ExclusiveStartKey });
    }, [queryParamsInHash]);
    useEffect(() => {
        getUpdateFn && getUpdateFn(forceUpdate);
    }, [getUpdateFn]);
    useEffect(() => {
        if (queryUuid) {
            debug && console.log(`DocClientDataGrid used effect on forceUpdate via new queryUuid`, queryUuid);
            forceUpdate();
        }
    }, [queryUuid]);
    useEffect(() => {
        debug && console.log('DocClientDataGrid rendering on ', queryParams?.providedRowObjects);
        debug && console.log(`Page: ${page}, ItemCount: ${itemsCount}`);
    }, [queryParamsHash]);
    const { state: [[selectedObjects, setSelectedObjects]], handlers: { addToSelectedObjects, removeFromSelectedObjects, isInSelectedObjects, togglePresenceInSelectedObjects }, } = useSelectedObjects([], items);
    const selectedObjectsHash = getObjectHash(selectedObjects);
    /** default behavior if not processRowUpdate is provided is to update the items locally */
    const defaultProcessRowUpdate = React.useCallback(async (newRow, oldRow) => {
        debug && console.log(`DocClientDataGrid using default processRowUpdate`, { newRow, oldRow });
        const itemsClone = [...items];
        const updatedIdx = itemsClone.findIndex((x) => x.id === newRow.id);
        if (updatedIdx !== -1) {
            const item = itemsClone[updatedIdx];
            const instance = Assembler.getInstance(item);
            Object.assign(instance, newRow);
            /** suppressed remove, not clear on what this is required for */
            /*
            const removed = itemsClone.splice(updatedIdx, 1, instance);
            debug && console.log(`defaultProcessRowUpdate spliced out from idx ${updatedIdx}`, removed);
            */
            addToSelectedObjects(instance);
            setItems(itemsClone);
        }
        else {
            debug && console.log(`defaultProcessRowUpdate cant find ${newRow.name} with id ${newRow.id}`);
        }
        const response = newRow;
        return response;
    }, [items]);
    useEffect(() => {
        if (onSelectedUpdate) {
            debug && console.log(`DocClientDataGrid calling onSelectedUpdate`, !!onSelectedUpdate);
            onSelectedUpdate(selectedObjects);
        }
    }, [selectedObjectsHash]);
    /** Selecting objects updates a REF!? */
    useEffect(() => {
        const updateSelectedTableObjectsRef = () => {
            if (selectedTableObjectsRef) {
                /** Note this does not cause a rerender in parent, but the current will have the correct selected objects */
                selectedTableObjectsRef.current = selectedObjects;
                debug && console.log(`DocClientDataGrid setting selectedTableObjects current to`, selectedObjects.length);
                if (dispatchDataGridHash) {
                    dispatchDataGridHash(selectedObjectsHash);
                    debug && console.log(`DocClientDataGrid dispatching dataGridHash ${selectedObjectsHash}`);
                }
                else {
                    debug && console.log(`DocClientDataGrid dispatchDataGridHash not present`);
                }
            }
            else {
                console.log(`no selectedTableObjectsRef updateSelectedTableObjectsRef`);
            }
        };
        updateSelectedTableObjectsRef();
    }, [selectedObjectsHash]);
    debug &&
        console.log('DocClientDataGrid', {
            page,
            provided: queryParamsIn?.providedRowObjects?.length,
            items: items?.length,
            queryParams,
            queryParamsHash,
        });
    useEffect(() => {
        dispatchCurrentItemCount?.(itemsCount);
    }, [itemsCount]);
    const getDataPage = (LastEvaluatedKey, page) => {
        if (queryParams)
            setQueryParams({ ...queryParams, ExclusiveStartKey: LastEvaluatedKey });
        debug && console.log(`Setting page to ${page}`);
        setPage(page);
    };
    let columnMap = viewDefinitionToGridColumnMap(viewDefinition, itemsEditable);
    /** 2024 06 retained in case change breaks functionality --- items used to initialize useSelectedObjects, shim goes to togglePresenceInSelected objects provided by hook */
    /* const togglePresenceInSelectedObjects = (v: boolean, id: string) => {
      const focusItem = items.find((item) => item.id === id);
      debug && console.log(`togglePresenceInSelectedObjects `, { found: focusItem, selectedObjects });
      if (focusItem) {
        isInSelectedObjects(focusItem) ? removeFromSelectedObjects(focusItem) : addToSelectedObjects(focusItem);
      }
    }; */
    const togglePresenceInSelectedObjectsShim = (v, id) => {
        togglePresenceInSelectedObjects(id);
    };
    const select = {
        valueGetter: (value, row, column, apiRef) => isInSelectedObjects({ id: row.id }),
        renderCell: (gridCellParams) => radioSelector({ gridCellParams, valueSetter: togglePresenceInSelectedObjectsShim }),
        renderHeader: (gridHeaderParams) => _jsx(SelectAll, { items, selectedObjects, setSelectedObjects }),
    };
    const rowsPerPage = Math.min(items.length, pageSize);
    if (itemsEditable || itemsSelectable !== false) {
        columnMap = { select, ...columnMap };
    }
    const rowToGridRow = (vob) => {
        return {
            select: isInSelectedObjects(vob),
            ...vob,
        };
    };
    const WAGItemCount = itemsCount * (LastEvaluatedKeys.length + 1) + rowsPerPage + 1;
    const paginationMode = LastEvaluatedKeys.length > 0 ? PaginationMode.SERVER : PaginationMode.CLIENT;
    const rowCount = paginationMode === PaginationMode.SERVER ? WAGItemCount : itemsCount;
    const [rowCountState, setRowCountState] = React.useState(0);
    const hideFooter = itemsCount < currentPageSize; // same as above TODO
    useEffect(() => {
        autosizeColumns &&
            apiRef.current?.autosizeColumns?.({
                includeHeaders: true,
                includeOutliers: true,
            });
        setRowCountState((prevRowCountState) => (rowCount !== undefined ? rowCount : prevRowCountState));
    }, [rowCount, setRowCountState]);
    /** This is unseemly and added by LSC Developer stream it applies the dubious row filter provided by Listerlizer
     * Renamed from constructRows
     */
    const constructRowsAndApplyFilter = () => {
        if (!filter || filter === '') {
            return items.map(rowToGridRow);
        }
        return items.filter((item) => JSON.stringify(item).toLocaleLowerCase().includes(filter.toLocaleLowerCase())).map(rowToGridRow);
    };
    const dataGridProps = {
        apiRef,
        checkboxSelection: false,
        columns: Object.entries(columnMap).map(([k, v]) => ({ field: k, ...v })),
        columnVisibilityModel,
        onColumnVisibilityModelChange,
        density: 'compact',
        disableRowSelectionOnClick: false,
        getRowClassName: (params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'),
        hideFooter,
        hideFooterPagination: itemsCount < currentPageSize,
        hideFooterSelectedRowCount: true,
        keepNonExistentRowsSelected: true,
        loading: queryHook.inFlight || loading,
        paginationModel: {
            page,
            pageSize: currentPageSize,
        },
        initialState: {
            sorting: {
                sortModel: initialSort,
            },
        },
        onPaginationModelChange: (paginationModel) => {
            setCurrentPageSize(paginationModel.pageSize);
            const LEK = LastEvaluatedKeys[paginationModel.page - 1];
            debug && console.log(`Page change ${paginationModel.page}, exclusiveStartKey`, { LastEvaluatedKeys });
            getDataPage(LEK, paginationModel.page);
        },
        pageSizeOptions: rowsPerPageOptions,
        processRowUpdate: processRowUpdate ?? defaultProcessRowUpdate,
        onProcessRowUpdateError,
        rowCount: paginationMode === PaginationMode.SERVER ? rowCountState : undefined,
        rows: constructRowsAndApplyFilter(),
        //onCellClick: handleCellClick,
        onCellEditStop: (params, event) => {
            debug && console.log(`DocClientDataGrid stop edit`, params.reason, params);
            if (params.reason === GridCellEditStopReasons.cellFocusOut) {
                event.defaultMuiPrevented = true;
            }
            else {
                // while this cast works, it probably is not the prescribed way of getting the input...
                debug && console.log(`DocClientDataGrid stop editing`, params.row, params.formattedValue, event.target.value); // this corresponds to the item
            }
        },
        onRowClick,
    };
    const handleCloseSnackbar = () => setSnackbar?.(null);
    return (_jsxs(_Fragment, { children: [_jsx(ErrorBoundary, { fallbackRender: () => _jsx(Typography, { children: "Error rendering data grid" }), onError: (error) => (onError && error instanceof Error ? onError(error) : undefined), children: _jsx(StripedDataGrid, { style: { fontSize: 'inherit' }, autoHeight: true, ...dataGridProps }) }), !!snackbar && (_jsx(Snackbar, { open: true,
                anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                onClose: handleCloseSnackbar,
                autoHideDuration: 6000, children: _jsx(Alert, { ...snackbar, onClose: handleCloseSnackbar }) }))] }));
}
